import { StrictMode } from "react";
import { BrowserRouter } from "react-router";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { createRoot } from "react-dom/client";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { broadcastQueryClient } from "core/broadcastQueryClient";
import { buildRedirectUri } from "core/auth/redirectUri";
import { getOfflineCacheKey } from "core/cache/offlineCache";
import { queryDefaultOptions } from "core/queryClientOptions";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider } from "react-oidc-context";
import { WebStorageStateStore, Log as OidcLog } from "oidc-client-ts";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { get as getItem, set as setItem, del as removeItem } from "idb-keyval";

import App from "./App";
import GlobalStyle from "./global-styles";
import ToastProvider from "core/context/ToastProvider";
import FlashProvider from "core/context/FlashProvider";
import NavigationProvider from "core/context/NavigationProvider";
import ThemeProvider from "core/context/ThemeProvider";

import "normalize.css";
import "react-loading-skeleton/dist/skeleton.css";
import "react-tooltip/dist/react-tooltip.css";

import { getEnv } from "common";
import { ENVIRONMENTS } from "common/utilities";
import { RootErrorBoundary } from "core/boundaries";
import ThemePicker from "core/components/ThemePicker";
import pkg from "../package.json";
import { Toaster } from "sonner";
import { toast } from "sonner";
import { faCircleX } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

const currentEnvironment = getEnv();

// On first login Keycloak renders a login form whose submission leaves the
// authorize URL in browser history. Pressing Back revisits it, and Keycloak
// (active SSO session) re-issues a callback echoing the original, already
// consumed `state`. oidc-client-ts's signinCallback then throws "No matching
// state found in storage", crashing into the AuthErrorBoundary.
//
// A genuine pending callback always has its state persisted by oidc-client-ts
// under `oidc.<state>`. If that entry is gone, this is a replayed/stale
// callback, so strip the params before AuthProvider tries to process them.
const recoverFromStaleAuthCallback = () => {
  const params = new URLSearchParams(window.location.search);
  const state = params.get("state");
  const isCallback = state && (params.get("code") || params.get("error"));

  if (!isCallback) return;

  const isPending = window.localStorage.getItem(`oidc.${state}`) !== null;

  if (!isPending) {
    window.history.replaceState({}, document.title, buildRedirectUri());
  }
};

recoverFromStaleAuthCallback();

const oidcSettings = {
  authority: import.meta.env.REACT_APP_OIDC_AUTH_URL,
  client_id: import.meta.env.REACT_APP_OIDC_CLIENT_ID,
  response_type: "code",
  scope: import.meta.env.REACT_APP_OIDC_SCOPE,
  redirect_uri: import.meta.env.REACT_APP_OIDC_REDIRECT_URL,
  post_logout_redirect_uri: import.meta.env.REACT_APP_OIDC_REDIRECT_URL,
  // Replace (not push) the authorize URL so pressing Back after login doesn't
  // revisit a now-expired Keycloak auth request.
  redirectMethod: "replace",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true, // default: true
  // Perform full sign out with revokeTokensOnSignout:true
  // https://authts.github.io/oidc-client-ts/interfaces/UserManagerSettings.html#revokeTokensOnSignout
  revokeTokensOnSignout: true, // default: false
  onSigninCallback: async (user) => {
    window.history.replaceState({}, document.title, buildRedirectUri());
  },
  monitorSession: false, // Cannot use this because keycloak issues with storageAccess in login-status-iframe
};

// Matt: Useful for monitoring internal user manager events
if (currentEnvironment !== ENVIRONMENTS.PROD) {
  OidcLog.logger = console;
}

// !!! IMPORTANT: TO get this working in localhost / vite dev
// - Set sample rates in .env to 100
datadogRum.init({
  applicationId: import.meta.env.REACT_APP_DATADOG_APP_ID,
  clientToken: import.meta.env.REACT_APP_DATADOG_CLIENT_TOKEN,
  site: import.meta.env.REACT_APP_DATADOG_SITE,
  service: import.meta.env.REACT_APP_DATADOG_SERVICE,
  env: import.meta.env.REACT_APP_DATADOG_ENV,
  version: pkg.version,
  sessionSampleRate: Number(import.meta.env.REACT_APP_DATADOG_SAMPLE_RATE),
  sessionReplaySampleRate: Number(
    import.meta.env.REACT_APP_DATADOG_REPLAY_SAMPLE_RATE
  ),
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  storeContextsAcrossPages: true,
  defaultPrivacyLevel: "mask-user-input",
  plugins: [reactPlugin({ router: true })],
  beforeSend: (event, context) => {
    if (event.type === "error") {
      console.debug("DATADOG_BEFORE_SEND", { event, context });
    }

    if (
      event.type === "error" &&
      event.error.message.includes(
        "ResizeObserver loop completed with undelivered notifications."
      )
    ) {
      return false;
    }

    return true;
  },
});

const queryCache = new QueryCache({
  onError: (error, query) => {
    console.error(error, query);

    if (query.meta.errorMessage) {
      toast.error("Error", {
        description: query.meta.errorMessage,
      });
    }
  },
  onSuccess: (data) => {},
  onSettled: (data, error) => {},
});
const queryClient = new QueryClient({
  queryCache,
  defaultOptions: queryDefaultOptions,
});

// Good resources for understanding limitations of using localStorage here instead of an asyncPersister
// https://rxdb.info/articles/localstorage.html
// https://dexie.org/
const OFFLINE_CACHE_KEY = getOfflineCacheKey();
const deprecatedOfflineCache = localStorage.getItem(OFFLINE_CACHE_KEY);
if (deprecatedOfflineCache) {
  console.log("Removing deprecated offline cache!");
  localStorage.removeItem(OFFLINE_CACHE_KEY);
}

const asyncStoragePersister = createAsyncStoragePersister({
  key: OFFLINE_CACHE_KEY,
  storage: {
    getItem: (key) => {
      // console.log("GET_ITEM", key);

      return getItem(key);
    },
    setItem: (key, value) => {
      // console.log("SET_ITEM", key);

      return setItem(key, value);
    },
    removeItem: (key) => {
      // console.log("REMOVE_ITEM", key);

      return removeItem(key);
    },
  },
  throttleTime: 100,
  retry: ({ persistedClient, error, errorCount }) => {
    console.log("Error while persisting query data.", {
      persistedClient,
      error,
      errorCount,
    });
    console.error(error);
    return undefined;
  },
  serialize: (queryClient) => {
    try {
      return JSON.stringify(queryClient);
    } catch (error) {
      console.error("Error serializing Query cache:", error);
      return "";
    }
  },
  deserialize: (cachedString) => {
    try {
      const parsedCache = JSON.parse(cachedString);
      return parsedCache;
    } catch (error) {
      console.error("Error deserializing Query cache:", error);
      return {};
    }
  },
});

window.queryClient = queryClient;

const persistOptions = {
  queryClient,
  maxAge: Infinity,
  persister: asyncStoragePersister,
  dehydrateOptions: {
    shouldRedactErrors: (error) => {
      return false;
    },
    shouldDehydrateQuery: (query) => {
      // Only persist queries that are explictly defined in each query
      if (query.meta?.persist) {
        const queryHasPromise = !!query.promise;
        const queryPromiseStatus = query.promise?.status ?? "";
        const queryData = query.state.data;
        // Question: What determines whether a query has a promise attached to it or not?
        // Answer: Queries that are stale from expired time or invalidation get their queryFn (aka promise) attached to the query object

        // If a query has a promise attached to it, then make sure its fulfilled
        // Either way only dehydrate (save to indexeddb) if the query has data.

        // If we simply return true here, we get:
        // - promise.then errors when refreshing a page with pending queries
        // - uncaught CancelledError instances when discarding the cache with pending queries
        // --- previously this was being caught by Axios, but after an update it appears this is still uncaught from within react-query itself (retryer.ts) https://github.com/TanStack/query/issues/9399 (open as of 10/21/2025)

        return queryHasPromise
          ? queryPromiseStatus === "fulfilled" && queryData
          : queryData;
      }

      return false;
    },
  },
};

// Experimental feature, locked to exact version to avoid minor/patch update regressions
// Resources:
// - https://tanstack.com/query/latest/docs/framework/react/plugins/broadcastQueryClient#usage
// - https://app.studyraid.com/en/read/11355/355117/cache-synchronization-across-tabs
const QUERY_BROADCAST_CHANNEL = `populi:${currentEnvironment}:query-broadcast`;
broadcastQueryClient({
  queryClient,
  broadcastChannel: QUERY_BROADCAST_CHANNEL,
});

const root = createRoot(document.getElementById("root"));

//   <StrictMode>
root.render(
  <ThemeProvider>
    <RootErrorBoundary>
      <BrowserRouter>
        <AuthProvider {...oidcSettings}>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={persistOptions}
            onSuccess={() => {
              console.debug("asyncStoragePersister restored!");
            }}
            onError={() => {
              console.log("asyncStoragePersister error!");
            }}
          >
            <FlashProvider>
              <ToastProvider>
                <NavigationProvider>
                  <NuqsAdapter>
                    <App />
                  </NuqsAdapter>
                </NavigationProvider>
              </ToastProvider>
            </FlashProvider>

            <ReactQueryDevtools initialIsOpen={false} />
          </PersistQueryClientProvider>
        </AuthProvider>
      </BrowserRouter>
    </RootErrorBoundary>

    <GlobalStyle />
    {/* <ThemePicker /> */}
    <Toaster
      // id="global"
      position="bottom-left"
      theme="light"
      richColors={true}
      toastOptions={{
        closeButton: true,
        classNames: {
          toast: "toast",
          title: "title",
          description: "description",
          actionButton: "action-button",
          cancelButton: "cancel-button",
          closeButton: "close-button",
          success: "success",
          error: "error",
          info: "info",
        },
      }}
      icons={{
        success: <FontAwesomeIcon icon={faCircleCheck} />,
        error: <FontAwesomeIcon icon={faCircleX} />,
        info: <FontAwesomeIcon icon={faCircleInfo} />,
      }}
    />
  </ThemeProvider>
);
{
  /* </StrictMode> */
}
